import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Scale, ExternalLink, FileText, CheckCircle, Clock,
  Archive, Loader2, ChevronLeft, ChevronRight, Eye, Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from '@/i18n/TranslationContext';
import { legalAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const statusStyles = {
  draft: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  approved: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
  archived: 'border-muted-foreground/30 text-muted-foreground bg-muted/20',
};

export default function LawsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const canDelete = user?.role === 'super_admin' || user?.role === 'legal_entry';
  const [laws, setLaws] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lawToDelete, setLawToDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    legalAPI.listCategories(true).then(res => setCategories(res.data.categories || [])).catch(() => {});
  }, []);

  const fetchLaws = useCallback(async () => {
    setLoading(true);
    try {
      const res = await legalAPI.listLaws({
        search, category: categoryFilter, status: statusFilter,
        limit: PAGE_SIZE, skip: page * PAGE_SIZE,
      });
      setLaws(res.data.laws || []);
      setTotal(res.data.total || 0);
    } catch { setLaws([]); }
    setLoading(false);
  }, [search, categoryFilter, statusFilter, page]);

  useEffect(() => { fetchLaws(); }, [fetchLaws]);

  const handleApprove = async (law) => {
    try {
      await legalAPI.approveLaw(law.law_id);
      toast.success('Law approved and activated');
      fetchLaws();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleArchive = async (law) => {
    try {
      await legalAPI.archiveLaw(law.law_id);
      toast.success(law.status === 'archived' ? 'Law restored to draft' : 'Law archived');
      fetchLaws();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    if (!lawToDelete) return;
    try {
      await legalAPI.deleteLaw(lawToDelete.law_id);
      toast.success('Law deleted successfully');
      setDeleteDialogOpen(false);
      setLawToDelete(null);
      fetchLaws();
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail;
      if (status === 403) toast.error('You do not have permission to delete laws');
      else if (status === 404) toast.error('Law not found');
      else if (status === 409) toast.error(msg || 'Cannot delete — law has scan results');
      else toast.error(msg || 'Failed to delete law');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="laws-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('laws_regulations')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('manage_laws')}</p>
        </div>
        <Button onClick={() => navigate('/laws/new')} data-testid="add-law-button">
          <Plus className="mr-2 h-4 w-4" />Add New Law
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t("search")} value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9 h-10 bg-muted/30 border-border" data-testid="laws-search" />
        </div>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-10 bg-muted/30 border-border" data-testid="laws-category-filter">
            <SelectValue placeholder={t("all_categories")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.category_id} value={c.title}>{c.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[140px] h-10 bg-muted/30 border-border" data-testid="laws-status-filter">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Jurisdiction</TableHead>
                <TableHead className="text-xs">Parameters</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : laws.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Scale className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('no_data')}</p>
                  </TableCell>
                </TableRow>
              ) : laws.map(law => (
                <TableRow key={law.law_id} data-testid="law-row" className="hover:bg-muted/20">
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{law.title}</p>
                      {law.source_url && (
                        <a href={law.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-0.5">
                          <ExternalLink className="h-3 w-3" />Source
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(law.categories || [law.category]).filter(Boolean).map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{law.jurisdiction || '\u2014'}</TableCell>
                  <TableCell>
                    <span className="text-sm font-mono">{(law.parameters || []).length}</span>
                    <span className="text-xs text-muted-foreground ml-1">rules</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('text-[10px] capitalize', statusStyles[law.status] || statusStyles.draft)}>
                      {law.status || 'draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/laws/${law.law_id}`)} data-testid="view-law-button">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {law.status === 'draft' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-vrs-green" onClick={() => handleApprove(law)} data-testid="approve-law-button">
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => handleArchive(law)} data-testid="archive-law-button">
                        {law.status === 'archived' ? <Clock className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { setLawToDelete(law); setDeleteDialogOpen(true); }} data-testid="delete-law-button">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Law Permanently</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>'{lawToDelete?.title}'</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setLawToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-law">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
